// MobileComandante — chat AI fullscreen ottimizzata
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Loader2, Check, AlertCircle, Bot, User, ListChecks, Mic } from "lucide-react";
import MobileShell from "@/components/mobile/MobileShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { buzz } from "@/lib/mobile";

const QUICK = [
  "Quanti clienti scaduti ho oggi?",
  "Chi sono i lead caldi senza data?",
  "Trova clienti con prestiti Santander",
  "Scadenzia gli arretrati 10 al giorno",
];

export default function MobileComandante() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [executing, setExecuting] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking]);

  const send = async (txt) => {
    const t = (txt || input).trim();
    if (!t) return;
    buzz();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: t }]);
    setThinking(true);
    try {
      const r = await api.post("/agent/chat", { session_id: sessionId, text: t });
      if (!sessionId) setSessionId(r.data.session_id);
      const parsed = r.data.response || {};
      setMessages((m) => [...m, { role: "assistant", parsed }]);
      if (parsed.kind === "plan" && parsed.plan && parsed.plan.needsConfirm === false) {
        await execute(parsed.plan, r.data.session_id || sessionId, true);
      }
    } catch (e) {
      toast.error("Errore AI");
      setMessages((m) => [...m, { role: "assistant", parsed: { kind: "answer", answer: "Errore: " + (e?.response?.data?.detail || e.message) } }]);
    } finally { setThinking(false); }
  };

  const execute = async (plan, sid, auto) => {
    setExecuting(true);
    try {
      const r = await api.post("/agent/execute", { session_id: sid, plan });
      setMessages((m) => [...m,
        { role: "results", results: r.data.results || [] },
        { role: "assistant", parsed: r.data.response || {} },
      ]);
      if (!auto) toast.success("Eseguito");
    } catch (e) { toast.error("Errore esecuzione"); }
    finally { setExecuting(false); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); setInput(""); };

  return (
    <MobileShell title="Comandante AI" subtitle="Powered by Claude 4.5"
      action={<button onClick={newChat} data-testid="mai-new" className="text-[#B7955B] text-sm font-semibold px-3 py-2 -my-2 rounded-lg active:bg-[#B7955B]/10">Nuova</button>}
      noPad
    >
      <div className="flex flex-col" style={{ minHeight: "calc(100vh - 64px - 64px)" }}>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="mai-messages">
          {messages.length === 0 ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto rounded-[10px] bg-gradient-to-br from-[#0A192F] to-[#172A45] flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-[#B7955B]" />
              </div>
              <h2 className="font-serif text-xl text-slate-900 mb-1">Cosa vuoi fare?</h2>
              <p className="text-xs text-slate-500 mb-4">Posso cercare, contare, scadenziare, aggiornare clienti.</p>
              <div className="space-y-2">
                {QUICK.map((p, i) => (
                  <button key={i} onClick={() => send(p)} data-testid={`mai-q-${i}`}
                          className="w-full text-left text-sm px-3 py-2.5 bg-white active:bg-[#F4F6FA]" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
                    <span className="text-[#B7955B] mr-1.5">▸</span>{p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => <MobileBubble key={i} m={m} onExecute={(plan) => execute(plan, sessionId, false)} executing={executing} />)
          )}
          {thinking && (
            <div className="flex items-center gap-2 text-slate-500 text-xs px-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Comandante sta pensando…
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input bar */}
        <div className="sticky bottom-0 bg-white/95 border-t border-[#E8ECF2] px-2 py-2">
          <div className="flex items-end gap-1.5 bg-slate-100 rounded-full pl-3 pr-1 py-1">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi un comando…"
              rows={1}
              data-testid="mai-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="flex-1 bg-transparent outline-none text-sm py-1.5 max-h-32 resize-none"
            />
            <button onClick={() => send()} disabled={!input.trim() || thinking || executing}
                    data-testid="mai-send"
                    className="bg-[#0A192F] text-white w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40 active:scale-95">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </MobileShell>
  );
}

function MobileBubble({ m, onExecute, executing }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-[#0A192F] text-white rounded-[10px] rounded-br-none px-3 py-2 max-w-[85%] text-sm">{m.text}</div>
      </div>
    );
  }
  if (m.role === "results") {
    return (
      <div className="flex justify-start">
        <div className="bg-emerald-50 border border-emerald-200 rounded-[10px] px-3 py-2 max-w-[90%] text-xs space-y-2">
          {(m.results || []).map((r, i) => (
            <div key={i}>
              <div className="text-emerald-800 font-semibold text-[10px] uppercase tracking-wider">{r.tool}</div>
              {r.error ? (
                <div className="text-rose-600">Errore: {r.error}</div>
              ) : (
                <ResultPreview tool={r.tool} result={r.result} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  const p = m.parsed || {};
  return (
    <div className="flex items-start gap-1.5">
      <div className="w-6 h-6 rounded-full bg-[#B7955B]/20 flex items-center justify-center shrink-0">
        <Bot className="w-3.5 h-3.5 text-[#B7955B]" />
      </div>
      <div className="max-w-[85%] space-y-1.5">
        {p.kind === "answer" && p.answer && (
          <div className="bg-white rounded-bl-none px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>{p.answer}</div>
        )}
        {p.kind === "ask" && p.ask && (
          <div className="bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-2 text-sm text-amber-900">
            <div className="text-[9px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-0.5"><AlertCircle className="w-2.5 h-2.5" /> Chiarimento</div>
            {p.ask}
          </div>
        )}
        {p.kind === "plan" && p.plan && <PlanCard plan={p.plan} onExecute={onExecute} executing={executing} />}
      </div>
    </div>
  );
}

function PlanCard({ plan, onExecute, executing }) {
  const [done, setDone] = useState(false);
  return (
    <div className={`border rounded-[10px] overflow-hidden ${plan.needsConfirm ? "border-[#B7955B] bg-[#F5EFE3]/40" : "border-slate-200 bg-white"}`}>
      <div className="px-3 py-2 border-b border-slate-100 bg-white">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#8a6e3d] font-semibold mb-0.5">
          <ListChecks className="w-2.5 h-2.5 text-rose-600" /> Piano {plan.needsConfirm && <span >— CONFERMA</span>}
        </div>
        <div className="text-xs text-slate-800">{plan.summary}</div>
      </div>
      <div className="px-3 py-1.5 space-y-0.5">
        {(plan.actions || []).map((a, i) => (
          <div key={i} className="text-[11px] flex items-start gap-1.5">
            <span className="text-[#B7955B] font-bold">{i + 1}.</span>
            <span className="flex-1 font-mono text-[10px] bg-slate-100 px-1 rounded-[10px]"><span >{a.tool}</span> {a.preview}</span>
          </div>
        ))}
      </div>
      {!done && (
        <div className="px-3 py-2 border-t border-slate-100 bg-white flex justify-end">
          <button onClick={() => { setDone(true); onExecute(plan); buzz(); }} disabled={executing}
                  className={`text-[11px] px-3 py-1.5 rounded-[10px] text-white flex items-center gap-1 ${plan.needsConfirm ? "bg-[#0A192F]" : "bg-emerald-600"} disabled:opacity-50`}>
            {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} {plan.needsConfirm ? "Conferma" : "Esegui"}
          </button>
        </div>
      )}
    </div>
  );
}

function ResultPreview({ tool, result }) {
  if (!result) return null;
  if (tool === "count_clients") return <div className="text-lg font-serif text-emerald-800">{result.total ?? 0}</div>;
  if (tool === "search_clients") {
    return (
      <div>
        <div className="text-emerald-800 font-semibold mb-1">{result.total ?? 0} trovati</div>
        {(result.items || []).slice(0, 5).map((c) => (
          <Link key={c.id} to={`/m/clienti/${c.id}`} className="block text-[10px] text-slate-700 truncate active:bg-emerald-100 px-1 rounded-[10px]">
            • {c.cognome} {c.nome} <span className="text-slate-400">({c.stato})</span>
          </Link>
        ))}
      </div>
    );
  }
  if (tool === "reschedule_clients") return <div>Ridistribuiti <strong>{result.updated}</strong> in {result.days_used} giorni</div>;
  if (tool === "update_clients") return <div>Aggiornati <strong>{result.updated}</strong></div>;
  if (tool === "list_portfolios") return <div>{result.portfolios?.length || 0} portafogli</div>;
  return null;
}
